/**
 * MapPlan — vue "Plan" (roadmap) Google Maps via l'Embed API.
 *
 * Même logique et mêmes coûts (= zéro) que StreetView : iframe Embed,
 * clé restreinte par referrer HTTP, pas de quota bloquant. On utilise le
 * mode `place` pour poser un pin sur l'adresse exacte — ça donne
 * immédiatement les POIs, rues nommées et contexte de quartier que la vue
 * cadastre / satellite ne montrent pas.
 *
 * On passe l'adresse textuelle en `q=` quand elle est dispo (meilleur rendu,
 * Google affiche le libellé propre dans le pin), et on tombe sur les
 * coordonnées brutes en fallback.
 */

import { Pill } from "./ui";
import { Ic } from "./icons";

const EMBED_URL = "https://www.google.com/maps/embed/v1/place";

export function MapPlan({
  lat,
  lng,
  label,
  zoom = 17,
  className = "",
}: {
  lat: number;
  lng: number;
  /** Libellé d'adresse si dispo — produit un pin mieux annoté. */
  label?: string | null;
  /** Niveau de zoom par défaut (Google : 0 = monde, 21 = maximum). */
  zoom?: number;
  className?: string;
}) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;

  if (!apiKey) {
    return (
      <div
        className={`relative rounded-xl2 overflow-hidden hairline bg-ink-50 flex items-center justify-center ${className}`}
      >
        <div className="max-w-[280px] text-center px-6">
          <div className="text-[13px] font-semibold text-ink-800">
            Clé Google Maps manquante
          </div>
          <div className="mt-1 text-[11.5px] text-ink-500 leading-relaxed">
            Configurez VITE_GOOGLE_MAPS_KEY pour activer la vue plan.
          </div>
        </div>
      </div>
    );
  }

  const q = label && label.trim() ? label : `${lat},${lng}`;
  const params = new URLSearchParams({
    key: apiKey,
    q,
    center: `${lat},${lng}`,
    zoom: String(zoom),
    maptype: "roadmap",
  });

  return (
    <div
      className={`relative rounded-xl2 overflow-hidden hairline bg-ink-50 ${className}`}
    >
      <iframe
        title="Plan (Google Maps)"
        src={`${EMBED_URL}?${params.toString()}`}
        className="absolute inset-0 w-full h-full border-0"
        loading="lazy"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <div className="absolute top-3 left-3 pointer-events-none">
        <Pill tone="soft" icon={<Ic.Pin size={12} />}>
          Plan — Google
        </Pill>
      </div>
    </div>
  );
}
